<template>
  <el-container direction="vertical" style="margin: 5vh 5vw">
    <job-edit @jobSubmitted="updateJob" :isVisible.sync="showEditModal" />
    <activity-new
      @activitySubmitted="createActivity"
      :isVisible.sync="showActivityAddModal"
    />
    <activity-edit
      @activitySubmitted="updateActivity"
      :activityEdit="activityEdit"
      :isVisible.sync="showActivityEditModal"
    />
    <h2>Package: {{ currentJobName }}</h2>
    <el-breadcrumb
      separator-class="el-icon-arrow-right"
      style="margin: 5px 0 35px 0"
    >
      <el-breadcrumb-item :to="{ path: `/servers/${instanceId}/packages` }"
        >Packages</el-breadcrumb-item
      >
      <el-breadcrumb-item>
        Edit Package
        <small>
          <el-tooltip
            placement="right"
            effect="light"
            content="Changes made to the package are saved
                            immediately."
          >
            <i
              class="el-icon-info"
              style="position: relative; left: 2px; bottom: 4px"
            />
          </el-tooltip>
        </small>
      </el-breadcrumb-item>
    </el-breadcrumb>
    <div
      style="display: flex; justify-content: space-between; margin-bottom: 12px"
    >
      <el-button-group>
        <el-button
          class="button"
          icon="el-icon-edit"
          style="margin: none"
          @click="showEditModal = true"
          v-if="envRights !== 'Run'"
          >Edit Name</el-button
        >
        <el-button class="button" icon="el-icon-caret-right" @click="runJob"
          >Run</el-button
        >
      </el-button-group>
    </div>
    <el-table
      :data="itemsToShow"
      style="border: 2px solid #ebeef5; width: 100%"
      max-height="60vh"
    >
      <el-table-column width="200">
        <template slot="header">
          <el-button
            @click="showActivityAddModal = true"
            class="button"
            :disabled="envRights === 'Run'"
            icon="el-icon-plus"
            >Add Activities</el-button
          >
        </template>
        <template slot-scope="scope">
          <el-button-group>
            <el-tooltip
              placement="bottom"
              content="Move Activity Up"
              v-if="scope.row.index !== 1 && envRights !== 'Run'"
            >
              <el-button
                size="medium"
                icon="el-icon-caret-top"
                class="table-button"
                @click="moveActivityUp(scope.row)"
            /></el-tooltip>
            <el-button
              size="medium"
              icon="el-icon-caret-top"
              class="hidden"
              v-else
            />
            <el-tooltip
              placement="bottom"
              content="Move Activity Down"
              v-if="scope.row.index !== items.length && envRights !== 'Run'"
            >
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-caret-bottom"
                @click="moveActivityDown(scope.row)"
            /></el-tooltip>
            <el-button
              size="medium"
              icon="el-icon-caret-bottom"
              class="hidden"
              v-else
            />
            <el-tooltip
              placement="bottom"
              :content="envRights === 'Run' ? 'View Activity' : 'Edit Activity'"
            >
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-edit"
                @click="
                  showActivityEditModal = true;
                  activityEdit = scope.row;
                "
            /></el-tooltip>
            <el-tooltip
              placement="bottom"
              content="Remove Activity"
              v-if="envRights !== 'Run'"
            >
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-close"
                @click="deleteActivity(scope.row)"
            /></el-tooltip>
            <el-button
              size="medium"
              icon="el-icon-close"
              class="hidden"
              v-else
            />
          </el-button-group>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="Name" :min-width="40" />
      <el-table-column prop="type" label="Type" :min-width="15" />
      <el-table-column prop="source" label="Source" :min-width="20" />
      <el-table-column
        prop="updatedAt"
        label="Last Modified At"
        :min-width="25"
      />
    </el-table>
  </el-container>
</template>

<script>
import { mapState } from "vuex";
import ActivityNew from "../components/modals/ActivityNew";
import ActivityEdit from "../components/modals/ActivityEdit";
import JobEdit from "../components/modals/JobEdit";
import JobService from "../services/JobService";
import moment from "moment-timezone";
import Helper from "../utilities/Helper";

export default {
  components: { ActivityNew, ActivityEdit, JobEdit },
  computed: {
    ...mapState([
      "jobId",
      "currentJobName",
      "instanceId",
      "envRights",
      "isUserLoggedIn",
      "environmentId",
      "userId",
    ]),
  },
  data() {
    return {
      itemsToShow: [],
      showEditModal: false,
      showActivityEditModal: false,
      showActivityAddModal: false,
      items: [],
      row: null,
      activityEdit: null,
      timezone:
        process.env.VUE_APP_SERVER_TIMEZONE ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  },
  methods: {
    handleError: Helper.handleError,
    trim: Helper.trim,
    async runJob() {
      if (
        confirm(
          `Are you sure you want to run the "${this.currentJobName}" package?`
        )
      ) {
        const loading = this.$loading({
          lock: true,
          //text: "Working",
          background: "rgba(0, 0, 0, 0.3)",
        });
        try {
          await JobService.run({
            jobId: this.jobId,
            userId: this.userId,
          });

          this.$message({
            type: "success",
            message: `The "${this.currentJobName}" package has been kicked off successfully. You'll receive an email when it finishes running. Check the package's history log for updates until then.`,
            duration: 5000,
            showClose: true,
          });
        } catch (err) {
          this.handleError(err, this, this.instanceId);
        } finally {
          loading.close();
        }
      }
    },
    async updateJob(name) {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        name = this.trim(name);
        await JobService.update({
          jobId: this.jobId,
          name,
        });
        this.$store.dispatch("setCurrentJobName", name);
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async showActivities() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        this.items.splice(0, this.items.length);
        const response = await JobService.showActivities({
          jobId: this.jobId,
        });
        response.data.jobActivities.forEach((activity) => {
          activity.updatedAt = moment(activity.updatedAt)
            .tz(this.timezone)
            .format("h:mm:ss A on LL");
          this.items.push(activity);
          this.itemsToShow = this.items;
        });
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async moveActivityUp(row) {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        await JobService.moveActivityUp({
          jobId: this.jobId,
          index: row.index,
        });
        this.showActivities();
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async moveActivityDown(row) {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        await JobService.moveActivityDown({
          jobId: this.jobId,
          index: row.index,
        });
        this.showActivities();
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async deleteActivity(row) {
      if (
        confirm(`Are you sure you want to remove the "${row.name}" activity?`)
      ) {
        const loading = this.$loading({
          lock: true,
          //text: "Working",
          background: "rgba(0, 0, 0, 0.3)",
        });
        try {
          await JobService.deleteActivity({
            jobId: this.jobId,
            index: row.index,
          });
          this.$message({
            type: "success",
            message: "Activity removed successfully",
            duration: 5000,
            showClose: true,
          });
          this.showActivities();
        } catch (err) {
          this.handleError(err, this, this.instanceId);
        } finally {
          loading.close();
        }
      }
    },
    async updateActivity({ type, selection }) {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        await JobService.updateActivity({
          jobId: this.jobId,
          index: this.activityEdit.index,
          type,
          selection,
        });
        this.$message({
          type: "success",
          message: "Activity updated successfully",
          duration: 5000,
          showClose: true,
        });
        this.showActivities();
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async createActivity({ type, selection }) {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        await JobService.createActivity({
          jobId: this.jobId,
          type,
          selection,
        });
        this.$message({
          type: "success",
          message: "Activity added successfully",
          duration: 5000,
          showClose: true,
        });
        this.showActivities();
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
  },
  mounted() {
    if (!this.isUserLoggedIn) {
      this.$router.push({ name: "login" });
    } else if (!this.instanceId) {
      this.$router.push({ name: "servers" });
    } else {
      this.showActivities();
    }
  },
  beforeDestroy() {
    if (this.jobId) this.$store.dispatch("setJobId", null);
    if (this.currentJobName) this.$store.dispatch("setCurrentJobName", null);
  },
};
</script>

<style scoped>
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
  padding: 10px 15px;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.table-button {
  background-color: inherit;
  color: #606266;
  border: none;
  outline: none;
  font-size: 1.15rem;
}
.table-button:focus {
  background-color: inherit;
  color: #606266;
  border: none;
  outline: none;
}
.table-button:hover {
  background-color: #ecf5fe;
  color: #606266;
  border: none;
  outline: none;
}
.hidden:focus {
  background-color: inherit;
  color: #f0f0f0;
  border: none;
  outline: none;
}
.hidden:hover {
  background-color: inherit;
  color: #f0f0f0;
  border: none;
  outline: none;
}
.hidden {
  background-color: inherit;
  color: #f0f0f0;
  border: none;
  outline: none;
  font-size: 1.15rem;
}
</style>
